import React, { useEffect, useId, useRef, useState } from 'react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

export interface SelectOption<T extends string | number = string> {
  value: T;
  label: string;
}

interface SelectProps<T extends string | number = string> {
  value: T;
  options: SelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  error?: boolean;
  'aria-label'?: string;
}

export function Select<T extends string | number = string>({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  disabled = false,
  className = '',
  buttonClassName = '',
  error = false,
  'aria-label': ariaLabel,
}: SelectProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`relative w-full rounded-2xl border bg-[#121214] px-4 py-3 pr-10 text-left text-sm transition focus:outline-none focus:ring-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60 ${
          error
            ? 'border-red-500/60 focus:ring-red-500/30 focus:border-red-500/50'
            : 'border-white/15 focus:ring-blue-500/40 focus:border-blue-500/40'
        } ${buttonClassName}`}
      >
        <span className={`block truncate ${selected ? 'text-white' : 'text-zinc-600'}`}>
          {selected?.label ?? placeholder}
        </span>
        <FiChevronDown
          size={16}
          className={`pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-zinc-500 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-50 mt-2 max-h-60 w-full overflow-y-auto rounded-2xl border border-white/10 bg-[#161618] p-1.5 shadow-[0_20px_50px_rgba(0,0,0,0.75)]"
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            return (
              <li key={String(option.value)} role="option" aria-selected={isSelected}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                  className={`flex w-full items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-left text-sm transition cursor-pointer ${
                    isSelected
                      ? 'bg-white/10 text-white'
                      : 'text-zinc-300 hover:bg-white/[0.06] hover:text-white'
                  }`}
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected && <FiCheck size={14} className="shrink-0 text-blue-400" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
